import React from "react";
import queryString from "query-string";
import { Table, Input } from "reactstrap";
import { FeedbackHistory } from "../../Models/FeedbackHistory";
import {
  LoadingState,
  ResponseErrorHelper,
  ApiRequest,
  LoadingStateHelper,
  ErrorDisplay,
  LoadingDisplay,
  DateDisplay
} from "../../../Common";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { StatusCode } from "../../../App/Enums/StatusCode";
import AuthorProfileBadge from "../../../User/Components/Common/AuthorProfileBadge";
import { AuthorType } from "../../../User/Constants/userEnums";
import "../../Styles/FeedbackHistoryContainer.scss";

type Props = {
  code: string;
};

type State = {
  filter: string;
  historyLoadingState: LoadingState;
  historyLoadingError?: ResponseErrorHelper;
  history: FeedbackHistory[];
};

const CLASSNAME = "FeedbackHistoryContainer";

export default class FeedbackHistoryContainer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      filter: "",
      historyLoadingState: LoadingStateHelper.generate(false, false, false),
      history: []
    };
  }

  componentDidMount = () => {
    this.loadHistory();
  };

  loadHistory = () => {
    const errorReason = "Failed to load feedback history";

    this.setState({
      historyLoadingState: LoadingStateHelper.generate(false, true, false)
    });

    ApiRequest.fromPath(this.assembleUrl())
      .withAuthHeader()
      .get()
      .then(response => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult<FeedbackHistory[]>) => {
            this.setState({
              history: result.payload,
              historyLoadingState: LoadingStateHelper.generate(false, true, true)
            });
          },
          result => {
            this.setState({
              historyLoadingState: LoadingStateHelper.generate(true, false, false),
              historyLoadingError: ResponseErrorHelper.generate(
                errorReason,
                result.error.message,
                response.status
              )
            });
          },
          () => {
            this.setState({
              historyLoadingState: LoadingStateHelper.generate(true, false, false),
              historyLoadingError: ResponseErrorHelper.generate(
                errorReason,
                "An unknown error has occured",
                response.status
              )
            });
          }
        ).catch(() => {
          this.setState({
            historyLoadingState: LoadingStateHelper.generate(true, false, false),
            historyLoadingError: ResponseErrorHelper.generate(
              errorReason,
              "Failed to parse JSON response",
              response.status
            )
          });
        });
      })
      .catch(() => {
        this.setState({
          historyLoadingState: LoadingStateHelper.generate(true, false, false),
          historyLoadingError: ResponseErrorHelper.generate(
            errorReason,
            "Unable to contact server",
            StatusCode.Status503ServiceUnavailable
          )
        });
      });
  };

  assembleUrl = () => {
    const params = {
      code: this.props.code
    };

    const stringParams = queryString.stringify(params);

    return `${endpoints.history.search()}?${stringParams}`;
  };

  filterRecords = () => {
    const keyword = this.state.filter.toLowerCase();

    return this.state.history.filter(
      record =>
        record.action.toLowerCase().includes(keyword) ||
        record.description.toLowerCase().includes(keyword) ||
        record.time.toLowerCase().includes(keyword) ||
        record.user.name.toLowerCase().includes(keyword)
    );
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ filter: event.currentTarget.value });

  render() {
    if (this.state.historyLoadingState.errored && !!this.state.historyLoadingError) {
      return <ErrorDisplay {...this.state.historyLoadingError} />;
    }

    if (this.state.historyLoadingState.isLoading && !this.state.historyLoadingState.isLoaded) {
      return <LoadingDisplay />;
    }

    const filteredRecords = this.filterRecords();

    return (
      <div className={CLASSNAME}>
        <div className="table-options">
          <span className="left">
            <h5>{filteredRecords.length} Records found</h5>
          </span>
          <span className="right">
            <Input
              className="filter"
              type="text"
              autoComplete="off"
              placeholder="Filter"
              onChange={this.onChange}
            />
          </span>
        </div>
        <div>
          <Table size="sm" className="table">
            <thead>
              <tr>
                <th className="small-cell">Action Type</th>
                <th className="small-cell">Author</th>
                <th className="medium-cell">Time</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                filteredRecords.map(entry => (
                  <tr key={entry.time}>
                    <td>{entry.action}</td>
                    <td>
                      {!!entry.user && (
                        <AuthorProfileBadge
                          authoredBy={{
                            type: AuthorType.User,
                            id: entry.user.username,
                            name: entry.user.name
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <DateDisplay date={new Date(Date.parse(entry.time))} />
                    </td>
                    <td>{entry.description}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ textAlign: "center" }} colSpan={4}>
                    <h4>No activity found</h4>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }
}
